import { useEffect, useState } from "react";

import PropTypes from "prop-types";
import Modal from "@components/ui-components/modal";
import Select from "@components/ui-components/select";
import { formatCurrency } from "@src/utility/helpers/utils";

const FIELD_CLASS = "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

const MODE_OPTIONS = ["NEFT", "RTGS", "IMPS", "SWIFT"];

const today = () => new Date().toISOString().split("T")[0];

// payment_date defaults to today rather than "" — an empty native date input
// visually renders today's date in some browsers (e.g. Safari's spinner), which
// looked filled while form.payment_date was still empty and silently kept the
// submit button disabled. Defaulting it for real keeps the UI honest.
const buildDefaultForm = () => ({ utr: "", payment_date: today(), mode: "NEFT" });

const UploadUtrModal = ({ open, onClose, publisherName, amount, onConfirm }) => {
    const [form, setForm] = useState(buildDefaultForm);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setForm(buildDefaultForm());
    }, [open]);

    const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
    const handleSelectChange = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm(form);
            onClose();
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Upload payment confirmation"
            subtitle={`${publisherName} · ${formatCurrency(amount)}`}
            footer={(
                <>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!form.utr  || !form.payment_date || submitting}
                        onClick={handleConfirm}
                        className="rounded-lg bg-navy px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50">
                        Mark paid
                    </button>
                </>
            )}>
            <div className="space-y-4">
                <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">UTR / reference number</label>
                    <input
                        type="text"
                        value={form.utr}
                        onChange={handleChange("utr")}
                        placeholder="e.g. UTR20260722001"
                        className={FIELD_CLASS} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700">Payment date</label>
                        <input
                            type="date"
                            max={today()}
                            value={form.payment_date}
                            onChange={handleChange("payment_date")}
                            className={FIELD_CLASS} />
                    </div>
                    <Select
                        id="payment-mode"
                        label="Mode"
                        className="w-full"
                        value={form.mode}
                        options={MODE_OPTIONS}
                        onChange={handleSelectChange("mode")} />
                </div>
            </div>
        </Modal>
    );
};

UploadUtrModal.propTypes = {
    open         : PropTypes.bool.isRequired,
    onClose      : PropTypes.func.isRequired,
    publisherName: PropTypes.string,
    amount       : PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onConfirm    : PropTypes.func.isRequired
};

export default UploadUtrModal;
